// LeadModal component - popup showing full lead details
// Opens when user clicks "View Details" on a lead


/**
 * 
 * NOTES-SUMMARY-LEAD MODAL
 * 
 * What this component does:
 *  - Shows full lead details in a popup
 *  - Displays contact info, enrichment data, AI analysis, and engagement timeline
 *  - Has "Generate Email" button that calls Claude AI
 *  - Shows generated email with copy button
 *  - Click outside or X button to close
 * 
 */



import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faCalendarAlt, faEnvelope, faStickyNote, faUser, faEdit, faSave, faTimes, faSync, faBookmark, faTrash, faCopy, faEye, faMousePointer, faReply, faFileText, faCalendarCheck, faPaperPlane, faCommentDots, faBan } from '@fortawesome/free-solid-svg-icons';
import Modal from '../common/Modal';
import { formatDateTime } from '../../utils/formatters';
import { generateEmail, updateLead, addLeadNote, saveEmailTemplate, getEmailTemplates, deleteEmailTemplate, deleteLead, getLead } from '../../utils/api';
import '../../styles/components.css';

// Module-level cache for lead details (persists across modal open/close within session)
const leadDetailsCache = new Map();

// Helper to update cache when lead data changes
const updateLeadCache = (leadId, leadData) => {
  if (leadId && leadData) {
    leadDetailsCache.set(leadId, { ...leadData, _cachedAt: Date.now() });
  }
};

// Helper to invalidate cache (e.g., after re-scoring)
const invalidateLeadCache = (leadId) => {
  leadDetailsCache.delete(leadId);
};

function LeadModal({ lead: initialLead, onClose, onLeadUpdate }) {
  // Full lead data (fetched on mount to get reasoning/next_action)
  const [lead, setLead] = useState(initialLead);
  const [loadingFullLead, setLoadingFullLead] = useState(true);

  const [generatedEmail, setGeneratedEmail] = useState('');
  const [generatedSubject, setGeneratedSubject] = useState('');
  const [generating, setGenerating] = useState(false);

  // Fetch full lead details on mount (with caching)
  useEffect(() => {
    const fetchFullLead = async () => {
      // Check cache first
      const cached = leadDetailsCache.get(initialLead._id);
      if (cached) {
        console.log('Using cached lead details for:', initialLead._id);
        setLead(cached);
        setLoadingFullLead(false);
        return;
      }

      // Not cached - fetch from API
      try {
        const fullLead = await getLead(initialLead._id);
        updateLeadCache(initialLead._id, fullLead);
        setLead(fullLead);
      } catch (error) {
        console.error('Failed to fetch full lead details:', error);
        // Keep using initialLead if fetch fails
      } finally {
        setLoadingFullLead(false);
      }
    };
    fetchFullLead();
  }, [initialLead._id]);
  
  // State for dialogs
  const [showUnqualifiedDialog, setShowUnqualifiedDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  
  // State for form inputs
  const [noteText, setNoteText] = useState('');
  const [selectedUser, setSelectedUser] = useState(lead.assigned_to || 'Unassigned');
  
  // State for loading states
  const [updating, setUpdating] = useState(false);
  const [reScoring, setReScoring] = useState(false);
  
  // State for editing
  const [editingContact, setEditingContact] = useState(false);
  const [editingEnrichment, setEditingEnrichment] = useState(false);
  const [editedContactData, setEditedContactData] = useState({});
  const [editedEnrichmentData, setEditedEnrichmentData] = useState({});
  
  // Modal state for alerts
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

  // State for expandable SMS messages
  const [expandedSmsMessages, setExpandedSmsMessages] = useState(new Set());

  // Email template states
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [saving, setSaving] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showTemplatesDropdown, setShowTemplatesDropdown] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Lead deletion states
  const [showDeleteLeadConfirm, setShowDeleteLeadConfirm] = useState(false);
  const [deletingLead, setDeletingLead] = useState(false);
  
  // User options
  const userOptions = ['Unassigned', 'Sean Foote', 'Stephen Welch'];

  // Toggle SMS message expansion
  const toggleSmsExpansion = (index) => {
    setExpandedSmsMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Render SMS message with expandable content
  const renderSmsMessage = (event, index) => {
    const message = event.metadata?.message_body || event.details || '';
    const maxLength = 150;
    const isLong = message.length > maxLength;
    const isExpanded = expandedSmsMessages.has(index);
    const displayMessage = (isLong && !isExpanded) ? message.slice(0, maxLength) + '...' : message;

    return (
      <div>
        <div className="timeline-details">{displayMessage || '—'}</div>
        {isLong && (
          <button
            onClick={() => toggleSmsExpansion(index)}
            style={{
              marginTop: '0.5rem',
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              color: '#3b82f6',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {isExpanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>
    );
  };

  // Handle generate email button
  const handleGenerateEmail = async () => {
    setGenerating(true);
    try {
      console.log('🎯 LeadModal: Generating email for:', lead.email);
      const result = await generateEmail(lead.email);
      console.log('✅ LeadModal: Got result:', result);
      setGeneratedEmail(result.email);
      setGeneratedSubject(result.subject || '');
    } catch (error) {
      console.error('❌ LeadModal: Error:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to generate email: ' + error.message,
        type: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };
  
  // Handle copy email to clipboard
  const handleCopyEmail = () => {
    const fullEmail = generatedSubject ? `Subject: ${generatedSubject}\n\n${generatedEmail}` : generatedEmail;
    navigator.clipboard.writeText(fullEmail);
    setModal({
      isOpen: true,
      title: 'Success',
      message: 'Email copied to clipboard!',
      type: 'success'
    });
  };
  
  // Handle re-score individual lead
  const handleReScoreLead = async () => {
    setReScoring(true);
    try {
      const response = await fetch('/.netlify/functions/score-leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ leadId: lead._id })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.scoredLead) {
        // Update the lead with new scoring data
        setLead(result.scoredLead);
        updateLeadCache(lead._id, result.scoredLead);
        if (onLeadUpdate) onLeadUpdate(result.scoredLead);
        setModal({
          isOpen: true,
          title: 'Success',
          message: 'Lead re-scored successfully!',
          type: 'success'
        });
      }
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to re-score lead: ' + error.message,
        type: 'error'
      });
    } finally {
      setReScoring(false);
    }
  };
  
  // Handle toggle qualified/unqualified
  const handleToggleQualified = async () => {
    setUpdating(true);
    try {
      let updates;
      let message;
      
      if (lead.stage === 'unqualified') {
        // Mark as qualified - restore original stage
        const originalStage = lead.original_stage || (lead.score < 75 ? 'cold' : 'engaged');
        updates = { stage: originalStage };
        message = 'Lead marked as qualified';
      } else {
        // Mark as unqualified - save current stage
        updates = { 
          original_stage: lead.stage,
          stage: 'unqualified' 
        };
        message = 'Lead marked as unqualified';
      }
      
      const result = await updateLead(lead._id, updates);
      setLead(result.lead);
      updateLeadCache(lead._id, result.lead);
      if (onLeadUpdate) onLeadUpdate(result.lead);
      setModal({
        isOpen: true,
        title: 'Success',
        message: message,
        type: 'success'
      });
      onClose();
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update lead: ' + error.message,
        type: 'error'
      });
    } finally {
      setUpdating(false);
      setShowUnqualifiedDialog(false);
    }
  };
  
  // Handle mark as qualified
  const handleMarkQualified = async () => {
    setUpdating(true);
    try {
      const updates = { stage: 'qualified' };
      const result = await updateLead(lead._id, updates);
      setLead(result.lead);
      updateLeadCache(lead._id, result.lead);
      if (onLeadUpdate) onLeadUpdate(result.lead);
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Lead marked as qualified',
        type: 'success'
      });
      onClose();
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update lead: ' + error.message,
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };
  
  // Handle add note
  const handleAddNote = async () => {
    if (!noteText.trim()) {
      setModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please enter a note',
        type: 'error'
      });
      return;
    }
    
    setUpdating(true);
    try {
      const result = await addLeadNote(lead._id, noteText.trim());
      setLead(result.lead);
      updateLeadCache(lead._id, result.lead);
      if (onLeadUpdate) onLeadUpdate(result.lead);
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Note added successfully',
        type: 'success'
      });
      setNoteText('');
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to add note: ' + error.message,
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };
  
  // Handle assign to user
  const handleAssignUser = async () => {
    setUpdating(true);
    try {
      const updates = {
        assigned_to: selectedUser === 'Unassigned' ? null : selectedUser
      };
      const result = await updateLead(lead._id, updates);
      setLead(result.lead);
      updateLeadCache(lead._id, result.lead);
      if (onLeadUpdate) onLeadUpdate(result.lead);
      setModal({
        isOpen: true,
        title: 'Success',
        message: `Lead assigned to ${selectedUser}`,
        type: 'success'
      });
      setShowAssignDialog(false);
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to assign lead: ' + error.message,
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };
  
  // Handle start editing contact
  const handleStartEditContact = () => {
    setEditingContact(true);
    setEditedContactData({
      phone: lead.phone || lead.enrichment?.phone || '',
      company: lead.company || lead.enrichment?.company || '',
    });
  };

  // Handle start editing enrichment
  const handleStartEditEnrichment = () => {
    setEditingEnrichment(true);
    setEditedEnrichmentData({
      company_size: lead.enrichment?.company_size || '',
      industry: lead.enrichment?.industry || '',
      job_title: lead.enrichment?.job_title || '',
      linkedin_url: lead.enrichment?.linkedin_url || '',
      tech_stack: lead.enrichment?.tech_stack || '',
    });
  };

  // Handle save contact changes
  const handleSaveContact = async () => {
    setUpdating(true);
    try {
      const updates = {
        phone: editedContactData.phone,
        company: editedContactData.company,
      };
      const result = await updateLead(lead._id, updates);
      setLead(result.lead);
      updateLeadCache(lead._id, result.lead);
      if (onLeadUpdate) onLeadUpdate(result.lead);
      setEditingContact(false);
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Contact information updated successfully',
        type: 'success'
      });
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update contact information: ' + error.message,
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle save enrichment changes
  const handleSaveEnrichment = async () => {
    setUpdating(true);
    try {
      const updates = {
        enrichment: {
          ...lead.enrichment,
          ...editedEnrichmentData
        }
      };
      const result = await updateLead(lead._id, updates);
      setLead(result.lead);
      updateLeadCache(lead._id, result.lead);
      if (onLeadUpdate) onLeadUpdate(result.lead);
      setEditingEnrichment(false);
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Enrichment data updated successfully',
        type: 'success'
      });
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update enrichment data: ' + error.message,
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle cancel editing
  const handleCancelEdit = (section) => {
    if (section === 'contact') {
      setEditingContact(false);
      setEditedContactData({});
    } else if (section === 'enrichment') {
      setEditingEnrichment(false);
      setEditedEnrichmentData({});
    }
  };

  // Handle save email as template
  const handleSaveTemplate = () => {
    if (!generatedEmail) return;
    setShowSaveTemplateDialog(true);
  };

  // Handle save template with name
  const handleSaveTemplateWithName = async () => {
    if (!templateName.trim()) {
      setModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please enter a template name',
        type: 'error'
      });
      return;
    }

    setSaving(true);
    try {
      const fullTemplate = generatedSubject ? `${generatedSubject}\n\n${generatedEmail}` : generatedEmail;
      await saveEmailTemplate(templateName.trim(), fullTemplate);
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Email template saved successfully!',
        type: 'success'
      });
      setShowSaveTemplateDialog(false);
      setTemplateName('');
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to save template: ' + error.message,
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle load email templates
  const handleLoadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await getEmailTemplates();
      setEmailTemplates(response.templates || []);
      setShowTemplatesDropdown(true);
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load templates: ' + error.message,
        type: 'error'
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handle select template
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplatesDropdown(false);
  };

  // Handle copy template content
  const handleCopyTemplate = () => {
    if (!selectedTemplate) return;
    navigator.clipboard.writeText(selectedTemplate.sanitizedText);
    setModal({
      isOpen: true,
      title: 'Success',
      message: 'Template copied to clipboard!',
      type: 'success'
    });
  };

  // Handle delete template
  const handleDeleteTemplate = (template) => {
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  };

  // Handle confirm delete template
  const handleConfirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    setDeleting(true);
    try {
      await deleteEmailTemplate(templateToDelete.id);
      setEmailTemplates(emailTemplates.filter(t => t.id !== templateToDelete.id));
      if (selectedTemplate && selectedTemplate.id === templateToDelete.id) {
        setSelectedTemplate(null);
      }
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Email template deleted successfully!',
        type: 'success'
      });
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to delete template: ' + error.message,
        type: 'error'
      });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    }
  };

  // Handle delete lead
  const handleDeleteLead = () => {
    setShowDeleteLeadConfirm(true);
  };

  // Handle confirm delete lead
  const handleConfirmDeleteLead = async () => {
    setDeletingLead(true);
    try {
      await deleteLead(lead._id, lead.email);

      // Remove from cache
      invalidateLeadCache(lead._id);

      // Show success message
      setModal({
        isOpen: true,
        title: 'Success',
        message: 'Lead deleted successfully',
        type: 'success'
      });
      
      // Close the modal and refresh data
      // The parent component should handle removing the lead from the list
      // This is signaled by passing null to onLeadUpdate (indicates deletion)
      if (onLeadUpdate) {
        onLeadUpdate(null, 'delete', lead._id);
      }
      
      // Close modal after short delay to allow user to see success message
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to delete lead: ' + error.message,
        type: 'error'
      });
    } finally {
      setDeletingLead(false);
      setShowDeleteLeadConfirm(false);
    }
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div>
            <h2>{lead.name}</h2>
            <div style={{ fontSize: '1.125rem', color: '#6b7280', marginTop: '0.25rem' }}>
              {lead.company || lead.enrichment?.company || 'N/A'}
            </div>
          </div>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>
        
        {/* Modal Body */}
        <div className="modal-body">
          {/* AI Analysis Section - Featured */}
          <section className="modal-section ai-analysis-section">
            <h3>
              <FontAwesomeIcon icon={faRobot} /> AI SCORE:
              <button 
                className="btn btn-sm btn-secondary"
                onClick={handleReScoreLead}
                disabled={reScoring}
                style={{ marginLeft: '1rem', fontSize: '0.875rem' }}
              >
                <FontAwesomeIcon icon={faSync} /> {reScoring ? 'Re-Scoring...' : 'Re-Score'}
              </button>
            </h3>
            <div className="ai-score-display">
              <span className="score-value">{lead.score ?? 0}/100</span>
            </div>
            <div className="ai-analysis-text">
              {loadingFullLead ? 'Loading AI analysis...' : (lead.reasoning || 'Lead scoring in progress. AI analysis will appear once engagement data is processed.')}
            </div>
            <div className="next-action-box">
              <span className="action-label">NEXT ACTION:</span>
              <p>{loadingFullLead ? 'Loading...' : (lead.next_action || 'Lead scoring in progress. Next action recommendations will appear after analyzing engagement activity.')}</p>
            </div>
          </section>

          {/* SMS Opt-Out Warning Banner */}
          {lead.sms_opted_out && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              padding: '1rem',
              margin: '1rem 0',
              borderRadius: '8px',
              color: '#991b1b',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <FontAwesomeIcon icon={faBan} />
              <div>
                <strong>SMS Opt-Out:</strong> This lead opted out of SMS communications
                {lead.sms_opted_out_at && ` on ${formatDateTime(lead.sms_opted_out_at)}`}.
                Do not contact via text message.
              </div>
            </div>
          )}

          {/* Two Column Layout - Contact Info and Enrichment */}
          <div className="modal-two-column">
            {/* Contact Info Section */}
            <section className="modal-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Contact Information</h3>
                {!editingContact ? (
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={handleStartEditContact}
                    disabled={updating}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <div>
                    <button 
                      className="btn btn-sm btn-success"
                      onClick={handleSaveContact}
                      disabled={updating}
                      style={{ marginRight: '8px' }}
                    >
                      <FontAwesomeIcon icon={faSave} /> Save
                    </button>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleCancelEdit('contact')}
                      disabled={updating}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </div>
                )}
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <span className="info-value">{lead.email}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Company</span>
                  {editingContact ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedContactData.company}
                      onChange={(e) => setEditedContactData({...editedContactData, company: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{lead.company || lead.enrichment?.company || '—'}</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Phone</span>
                  {editingContact ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedContactData.phone}
                      onChange={(e) => setEditedContactData({...editedContactData, phone: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{lead.phone || lead.enrichment?.phone || '—'}</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Assigned To</span>
                  <span className="info-value" style={{ color: lead.assigned_to ? '#374151' : '#9ca3af' }}>
                    {lead.assigned_to || 'Unassigned'}
                  </span>
                </div>
              </div>
            </section>
            
            {/* Enrichment Data Section */}
            <section className="modal-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Enrichment Data</h3>
                {!editingEnrichment ? (
                  <button 
                    className="btn btn-sm btn-secondary"
                    onClick={handleStartEditEnrichment}
                    disabled={updating}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                ) : (
                  <div>
                    <button 
                      className="btn btn-sm btn-success"
                      onClick={handleSaveEnrichment}
                      disabled={updating}
                      style={{ marginRight: '8px' }}
                    >
                      <FontAwesomeIcon icon={faSave} /> Save
                    </button>
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleCancelEdit('enrichment')}
                      disabled={updating}
                    >
                      <FontAwesomeIcon icon={faTimes} /> Cancel
                    </button>
                  </div>
                )}
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Company Size</span>
                  {editingEnrichment ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedEnrichmentData.company_size}
                      onChange={(e) => setEditedEnrichmentData({...editedEnrichmentData, company_size: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{lead.enrichment?.company_size || '—'}</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Industry</span>
                  {editingEnrichment ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedEnrichmentData.industry}
                      onChange={(e) => setEditedEnrichmentData({...editedEnrichmentData, industry: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{lead.enrichment?.industry || '—'}</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Job Title</span>
                  {editingEnrichment ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedEnrichmentData.job_title}
                      onChange={(e) => setEditedEnrichmentData({...editedEnrichmentData, job_title: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{lead.enrichment?.job_title || '—'}</span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">LinkedIn</span>
                  {editingEnrichment ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedEnrichmentData.linkedin_url}
                      onChange={(e) => setEditedEnrichmentData({...editedEnrichmentData, linkedin_url: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">
                      {lead.enrichment?.linkedin_url ? (
                        <a href={lead.enrichment.linkedin_url} target="_blank" rel="noopener noreferrer">
                          {lead.enrichment.linkedin_url}
                        </a>
                      ) : '—'}
                    </span>
                  )}
                </div>
                <div className="info-item">
                  <span className="info-label">Tech Stack</span>
                  {editingEnrichment ? (
                    <input
                      type="text"
                      className="info-input"
                      value={editedEnrichmentData.tech_stack}
                      onChange={(e) => setEditedEnrichmentData({...editedEnrichmentData, tech_stack: e.target.value})}
                    />
                  ) : (
                    <span className="info-value">{lead.enrichment?.tech_stack || '—'}</span>
                  )}
                </div>
              </div>
            </section>
          </div>
          
          {/* Engagement Timeline Section */}
          <section className="modal-section timeline-section">
            <h3>
              <FontAwesomeIcon icon={faCalendarAlt} className="timeline-icon" />
              Engagement Timeline
            </h3>
            <div className="timeline">
              {lead.engagement && lead.engagement.length > 0 ? (
                lead.engagement
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-date">{formatDateTime(event.date)}</div>
                      <div className="timeline-content">
                        <div className="timeline-type">
                          {(event.type === 'email_open' || event.type === 'email_opened') && <FontAwesomeIcon icon={faEye} style={{marginRight: '0.5rem'}} />}
                          {(event.type === 'link_click' || event.type === 'link_clicked') && <FontAwesomeIcon icon={faMousePointer} style={{marginRight: '0.5rem'}} />}
                          {(event.type === 'email_reply' || event.type === 'email_replied') && <FontAwesomeIcon icon={faReply} style={{marginRight: '0.5rem'}} />}
                          {event.type === 'form_submit' && <FontAwesomeIcon icon={faFileText} style={{marginRight: '0.5rem'}} />}
                          {event.type === 'form_submitted' && <FontAwesomeIcon icon={faFileText} style={{marginRight: '0.5rem'}} />}
                          {event.type === 'meeting_booking' && <FontAwesomeIcon icon={faCalendarCheck} style={{marginRight: '0.5rem'}} />}
                          {event.type === 'first_contact' && <FontAwesomeIcon icon={faEnvelope} style={{marginRight: '0.5rem'}} />}
                          {(event.type === 'email_sent' || event.type === 'email_send') && <FontAwesomeIcon icon={faPaperPlane} style={{marginRight: '0.5rem'}} />}
                          {event.type === 'hubspot_email_open' && <FontAwesomeIcon icon={faEye} style={{marginRight: '0.5rem'}} />}
                          {event.type === 'hubspot_email_click' && <FontAwesomeIcon icon={faMousePointer} style={{marginRight: '0.5rem'}} />}
                          {event.type === 'hubspot_email_reply' && <FontAwesomeIcon icon={faReply} style={{marginRight: '0.5rem'}} />}
                          {event.type === 'sms_sent' && <FontAwesomeIcon icon={faCommentDots} style={{marginRight: '0.5rem', color: '#3b82f6'}} />}
                          {event.type === 'sms_reply' && <FontAwesomeIcon icon={faCommentDots} style={{marginRight: '0.5rem', color: '#10b981'}} />}
                          {event.type === 'appointment_booked' && <FontAwesomeIcon icon={faCalendarCheck} style={{marginRight: '0.5rem', color: '#8b5cf6'}} />}
                          {event.type === 'appointment_completed' && <FontAwesomeIcon icon={faCalendarCheck} style={{marginRight: '0.5rem', color: '#10b981'}} />}
                          {event.type === 'appointment_no_show' && <FontAwesomeIcon icon={faCalendarCheck} style={{marginRight: '0.5rem', color: '#ef4444'}} />}
                          {event.type === 'sms_opt_out' && <FontAwesomeIcon icon={faBan} style={{marginRight: '0.5rem', color: '#ef4444'}} />}
                          {event.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          {event.type === 'sms_reply' && event.metadata?.sentiment && (
                            <span className={`sentiment-badge sentiment-${event.metadata.sentiment}`}>
                              {event.metadata.sentiment}
                            </span>
                          )}
                        </div>
                        {/* Show expandable SMS messages for SMS events */}
                        {(event.type === 'sms_sent' || event.type === 'sms_reply') ? (
                          renderSmsMessage(event, index)
                        ) : (
                          <div className="timeline-details">{event.details || '—'}</div>
                        )}
                        <div className="timeline-source">via {event.source || 'system'}</div>
                      </div>
                    </div>
                  ))
              ) : (
                <p className="no-engagement">No engagement activity recorded yet.</p>
              )}
            </div>
          </section>
          
          {/* Notes Section */}
          <section className="modal-section notes-section">
            <div className="section-header">
              <h3>
                <FontAwesomeIcon icon={faStickyNote} /> Notes
              </h3>
            </div>

            {/* Add Note Form - ALWAYS VISIBLE */}
            <div className="add-note-form">
              <textarea
                className="note-input"
                placeholder="Add a new note..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
              />
              <button 
                className="add-note-button"
                onClick={handleAddNote}
                disabled={!noteText.trim() || updating}
              >
                <FontAwesomeIcon icon={faStickyNote} /> {updating ? 'Adding...' : 'Add Note'}
              </button>
            </div>

            {/* Existing Notes Display */}
            <div className="notes-list">
              {lead.notes && lead.notes.length > 0 ? (
                lead.notes
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((note, index) => (
                    <div key={index} className="note-item">
                      <div className="note-header">
                        <span className="note-author">{note.created_by}</span>
                        <span className="note-date">{formatDateTime(note.created_at)}</span>
                      </div>
                      <div className="note-text">{note.text}</div>
                    </div>
                  ))
              ) : (
                <p className="no-notes">No notes yet. Add your first note above!</p>
              )}
            </div>
          </section>
          
          {/* Email Generator Section */}
          <section className="modal-section email-generator-section">
            <h3>
              <FontAwesomeIcon icon={faEnvelope} /> Generate Personalized Email
            </h3>
            
            {/* Generate and Saved Templates buttons */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <button 
                onClick={handleGenerateEmail} 
                className="btn btn-primary"
                disabled={generating}
              >
                {generating ? 'Generating...' : 'Generate Email with AI'}
              </button>
              
              <button 
                onClick={handleLoadTemplates} 
                className="btn btn-secondary"
                disabled={loadingTemplates}
              >
                <FontAwesomeIcon icon={faEnvelope} /> {loadingTemplates ? 'Loading...' : 'Saved Email Templates'}
              </button>
            </div>
            
            {generatedEmail && (
              <div className="generated-email">
                <div className="email-header">
                  <h4>Generated Email:</h4>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={handleCopyEmail} className="btn btn-sm btn-secondary">
                      <FontAwesomeIcon icon={faCopy} /> Copy to Clipboard
                    </button>
                    <button onClick={handleSaveTemplate} className="btn btn-sm btn-success">
                      <FontAwesomeIcon icon={faBookmark} /> Save
                    </button>
                  </div>
                </div>
                {generatedSubject && (
                  <div className="email-subject">
                    <strong>Subject:</strong> {generatedSubject}
                  </div>
                )}
                <div className="email-body">
                  {generatedEmail}
                </div>
              </div>
            )}

            {/* Saved Templates Dropdown */}
            {showTemplatesDropdown && (
              <div className="email-templates-dropdown" style={{ 
                marginTop: '1rem', 
                border: '1px solid #d97706', 
                borderRadius: '8px', 
                background: 'white',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <div style={{ 
                  padding: '0.75rem', 
                  background: '#fbbf24', 
                  borderBottom: '1px solid #d97706',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <strong style={{ color: '#92400e' }}>Saved Email Templates</strong>
                  <button 
                    onClick={() => setShowTemplatesDropdown(false)}
                    className="btn btn-sm btn-secondary"
                    style={{ padding: '0.25rem 0.5rem' }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div style={{ padding: '0.5rem' }}>
                  {!emailTemplates || !Array.isArray(emailTemplates) || emailTemplates.length === 0 ? (
                    <p style={{ margin: 0, padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
                      No saved templates yet.
                    </p>
                  ) : (
                    emailTemplates.map((template) => (
                      <div 
                        key={template.id} 
                        style={{ 
                          padding: '0.75rem', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '6px', 
                          marginBottom: '0.5rem',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <div style={{ fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                          {template.name}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                          {template.sanitizedText?.substring(0, 100)}...
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Selected Template Display */}
            {selectedTemplate && (
              <div className="selected-template" style={{ 
                marginTop: '1rem', 
                border: '1px solid #d97706', 
                borderRadius: '8px', 
                background: 'white' 
              }}>
                <div className="email-header">
                  <h4>Template: {selectedTemplate.name}</h4>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button onClick={handleCopyTemplate} className="btn btn-sm btn-secondary">
                      <FontAwesomeIcon icon={faCopy} /> Copy Template
                    </button>
                    <button 
                      onClick={() => handleDeleteTemplate(selectedTemplate)} 
                      className="btn btn-sm btn-warning"
                      style={{ background: '#ef4444', color: 'white' }}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Delete
                    </button>
                  </div>
                </div>
                <div className="email-body" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedTemplate.sanitizedText}
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Modal Actions */}
        <div className="modal-actions">
          <button 
            className="action-btn assign-btn"
            onClick={() => setShowAssignDialog(true)}
            disabled={updating}
          >
            <FontAwesomeIcon icon={faUser} /> Assign to User
          </button>
          
          <button 
            className="action-btn qualified-btn"
            onClick={handleMarkQualified}
            disabled={updating}
          >
            <FontAwesomeIcon icon={faUser} /> Mark Qualified
          </button>
          
          <button 
            className="action-btn unqualified-btn"
            onClick={() => setShowUnqualifiedDialog(true)}
            disabled={updating}
          >
            <FontAwesomeIcon icon={faUser} /> {lead.stage === 'unqualified' ? 'Mark Qualified' : 'Mark Unqualified'}
          </button>
          
          <button 
            className="action-btn delete-btn"
            onClick={handleDeleteLead}
            disabled={updating || deletingLead}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: '1px solid #dc2626'
            }}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete Lead
          </button>
        </div>
        
        {/* Confirmation Dialogs */}
        {showUnqualifiedDialog && (
          <div className="modal-overlay" onClick={() => setShowUnqualifiedDialog(false)}>
            <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Action</h3>
              <p>Are you sure you want to mark this lead as {lead.stage === 'unqualified' ? 'qualified' : 'unqualified'}?</p>
              <div className="dialog-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowUnqualifiedDialog(false)}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-warning"
                  onClick={handleToggleQualified}
                  disabled={updating}
                >
                  {updating ? 'Updating...' : (lead.stage === 'unqualified' ? 'Mark Qualified' : 'Mark Unqualified')}
                </button>
              </div>
            </div>
          </div>
        )}
        
        
        {showAssignDialog && (
          <div className="modal-overlay" onClick={() => setShowAssignDialog(false)}>
            <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
              <h3>Assign to User</h3>
              <select
                className="user-select"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
              >
                {userOptions.map(user => (
                  <option key={user} value={user}>{user}</option>
                ))}
              </select>
              <div className="dialog-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAssignDialog(false);
                    setSelectedUser(lead.assigned_to || 'Unassigned');
                  }}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-success"
                  onClick={handleAssignUser}
                  disabled={updating}
                >
                  {updating ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Save Template Dialog */}
        {showSaveTemplateDialog && (
          <div className="modal-overlay" onClick={() => setShowSaveTemplateDialog(false)}>
            <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
              <h3>Save Email Template</h3>
              <p>Enter a name for this email template:</p>
              <input
                type="text"
                className="info-input"
                placeholder="Template name (e.g., 'Follow-up Email')"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                style={{ width: '100%', marginBottom: '1rem' }}
              />
              <div className="dialog-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowSaveTemplateDialog(false);
                    setTemplateName('');
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-success"
                  onClick={handleSaveTemplateWithName}
                  disabled={saving || !templateName.trim()}
                >
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Template Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Email Template</h3>
              <p>Are you sure you want to delete this email template? This action cannot be undone.</p>
              <div style={{ 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '6px', 
                padding: '0.75rem', 
                margin: '1rem 0',
                color: '#991b1b'
              }}>
                <strong>Template:</strong> {templateToDelete?.name}
              </div>
              <div className="dialog-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setTemplateToDelete(null);
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-warning"
                  onClick={handleConfirmDeleteTemplate}
                  disabled={deleting}
                  style={{ background: '#ef4444', color: 'white' }}
                >
                  {deleting ? 'Deleting...' : 'Delete Template'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Lead Confirmation Dialog */}
        {showDeleteLeadConfirm && (
          <div className="modal-overlay" onClick={() => setShowDeleteLeadConfirm(false)}>
            <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Lead</h3>
              <p>Are you sure you want to delete this lead? This action cannot be undone.</p>
              <div style={{ 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '6px', 
                padding: '0.75rem', 
                margin: '1rem 0',
                color: '#991b1b'
              }}>
                <strong>Lead:</strong> {lead.name} ({lead.email})<br />
                <strong>Company:</strong> {lead.company || lead.enrichment?.company || 'N/A'}
              </div>
              <div className="dialog-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteLeadConfirm(false)}
                  disabled={deletingLead}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-warning"
                  onClick={handleConfirmDeleteLead}
                  disabled={deletingLead}
                  style={{ background: '#ef4444', color: 'white' }}
                >
                  {deletingLead ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Notification Modal */}
        <Modal
          isOpen={modal.isOpen}
          onClose={() => setModal({ ...modal, isOpen: false })}
          title={modal.title}
          message={modal.message}
          type={modal.type}
        />
      </div>
    </div>
  );
}

export default LeadModal;